/**
 * Die **Musteranlage**: der Dialog, in dem ein Fragmentmuster entsteht — Kennung,
 * Codefragment, Beispielpaar, Name, Meldungstext, Art und Schwere (ADR-0033,
 * zweite Etappe).
 *
 * Er steht hier und nicht mehr in der Musterverwaltung, weil ihn zwei Flächen
 * öffnen: die Verwaltung, in der von Hand angelegt wird, und die **Befundansicht**,
 * in der der Knopf „Muster daraus machen" am Befund steht. Es ist derselbe
 * Dialog und nicht ein zweiter, der ihm ähnlich sieht — sonst hätte die eine
 * Fläche eine Pflicht, die die andere nicht kennt.
 *
 * **Der Entwurf aus einem Befund belegt zwei Felder vor und keines mehr**
 * (`from`): Fragment und Positivbeispiel tragen den Codeausschnitt der Stelle.
 * Die Platzhalter setzt ein Mensch — ein Vorschlag, welcher Teil das
 * Veränderliche ist, wäre genau die Behauptung, die niemand geprüft hat. Das
 * Negativbeispiel kann aus dem Befund nicht kommen: Er liefert die Stelle, an der
 * die Form falsch ist, nicht die, an der sie richtig wäre. Und Name, Meldungstext,
 * Art und Schwere bleiben leer, weil der Text des Befundes die Aussage über
 * *diese* Stelle ist und ein Muster über alle spricht.
 *
 * **Auch Art und Schwere stehen dann unbelegt da** und nicht auf dem ersten
 * Eintrag ihrer Liste. Eine Vorauswahl ließe sich mitspeichern, ohne dass jemand
 * sie getroffen hat — und sie stünde danach an jedem Befund dieses Musters.
 * Vorbelegt bleiben sie allein da, wo von Hand angelegt wird: Dort ist die Wahl
 * der erste Schritt und nicht der übersehene.
 *
 * **Die Prüfung des Beispielpaars steht im Server**, und hier steht ihre Antwort.
 * Der Knopf löst nicht aus, solange eines der Pflichtfelder leer ist — das ersetzt
 * die Prüfung nicht, es erspart nur die Fehlermeldung für den offensichtlichen
 * Fall. Ob das Muster seinen eigenen Fall trifft, entscheidet dort der Vergleicher
 * und nicht dieses Formular.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { Button, Dialog, FormGrid, FormRow, Modal, Select, TextField } from "../../design-system";
import type { NewControlPattern, PatternDraft } from "../../api/client";
import { Notice, describe } from "../loading";

/** Was in einem Auswahlfeld steht, das noch niemand gewählt hat. */
const UNCHOSEN = "Bitte wählen";

const HAND = "Die Erkennung ist ein Codefragment mit Platzhaltern: $_ trifft irgendeinen Ausdruck, "
  + "$_name trifft ebenso einen und verlangt an jeder weiteren Stelle denselben. Dazu gehört das "
  + "Beispielpaar — ein Schnipsel, den das Muster treffen muss, und einer, den es nicht treffen darf. "
  + "Gespeichert wird nur, was beides besteht.";

const FROM_FINDING = "Fragment und Positivbeispiel stehen so da, wie der Code an der Stelle steht. "
  + "Die Platzhalter setzen Sie: $_ trifft irgendeinen Ausdruck, $_name trifft ebenso einen und "
  + "verlangt an jeder weiteren Stelle denselben. Das Negativbeispiel, der Name, der Meldungstext, "
  + "Art und Schwere kommen aus dem Befund nicht — er sagt etwas über diese eine Stelle, ein Muster "
  + "spricht über alle.";

export function NewPatternDialog({
  kinds,
  severities,
  from = null,
  create,
  close,
  created,
}: {
  kinds: string[];
  severities: string[];
  /**
   * Der Entwurf aus einem Befund — `null` heißt: von Hand angelegt, alle Felder
   * leer.
   */
  from?: PatternDraft | null;
  create: (draft: NewControlPattern) => Promise<unknown>;
  close: () => void;
  created: () => void;
}): ReactElement {
  const [identifier, setIdentifier] = useState("");
  const [label, setLabel] = useState("");
  const [message, setMessage] = useState("");
  // Aus einem Befund heraus unbelegt, von Hand vorbelegt — der Unterschied steht
  // oben und ist die Aussage dieses Dialogs.
  const [kind, setKind] = useState(from === null ? kinds[0] ?? "" : "");
  const [severity, setSeverity] = useState(from === null ? severities[0] ?? "" : "");
  const [fragment, setFragment] = useState(from?.fragment ?? "");
  const [positive, setPositive] = useState(from?.positiveExample ?? "");
  const [negative, setNegative] = useState("");
  const [failure, setFailure] = useState<string | null>(null);

  const incomplete = [identifier, label, message, fragment, positive, negative, kind, severity].some(
    (value) => value.trim() === "",
  );

  return (
    <Modal onClose={close}>
      <Dialog
        title={from === null ? "Kontrollmuster anlegen" : "Kontrollmuster aus diesem Befund"}
        description={from === null ? HAND : FROM_FINDING}
        width={640}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              size="lg"
              disabled={incomplete}
              onClick={() => {
                setFailure(null);
                create({
                  identifier,
                  fragment,
                  positiveExample: positive,
                  negativeExample: negative,
                  label,
                  message,
                  kind,
                  severity,
                  // Der Ursprung geht mit hinaus, wo es einen gibt: Am Muster soll
                  // stehen, aus welchem Befund es entstanden ist.
                  ...(from === null ? {} : { originFindingId: from.finding.id }),
                }).then(created, (error: unknown) => setFailure(describe(error)));
              }}
            >
              Anlegen
            </Button>
          </>
        }
      >
        <FormGrid>
          {/* Woher die Vorbelegung kommt, steht als Auskunft dabei — sonst steht
              fremder Code im Formular, und niemand weiß, aus welcher Datei. */}
          {from === null ? null : (
            <FormRow label="Aus dem Befund">
              <span style={{ fontSize: "var(--fs-12)", color: "var(--fg-6)" }}>
                {siteOf(from)}
              </span>
            </FormRow>
          )}
          <FormRow label="Kennung" htmlFor="new-control-pattern-identifier">
            <TextField
              id="new-control-pattern-identifier"
              value={identifier}
              placeholder="comparison-with-itself"
              onChange={setIdentifier}
            />
          </FormRow>
          <FormRow label="Name" htmlFor="new-control-pattern-label">
            <TextField
              id="new-control-pattern-label"
              value={label}
              placeholder="Vergleich mit sich selbst"
              onChange={setLabel}
            />
          </FormRow>
          <FormRow label="Befundart" htmlFor="new-control-pattern-kind">
            <Select options={kinds} value={kind} placeholder={UNCHOSEN} onSelect={setKind} />
          </FormRow>
          <FormRow label="Schweregrad" htmlFor="new-control-pattern-severity">
            <Select
              options={severities}
              value={severity}
              placeholder={UNCHOSEN}
              onSelect={setSeverity}
            />
          </FormRow>
          <MultilineRow
            id="new-control-pattern-fragment"
            label="Codefragment"
            value={fragment}
            onChange={setFragment}
            mono
          />
          <MultilineRow
            id="new-control-pattern-message"
            label="Meldungstext"
            value={message}
            onChange={setMessage}
          />
          <MultilineRow
            id="new-control-pattern-positive"
            label="Positivbeispiel"
            value={positive}
            onChange={setPositive}
            mono
          />
          <MultilineRow
            id="new-control-pattern-negative"
            label="Negativbeispiel"
            value={negative}
            onChange={setNegative}
            mono
          />
        </FormGrid>

        <div style={{ paddingTop: "var(--sp-4)" }}>
          {failure === null ? (
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              Ohne Gegenprobe behauptet ein Muster, es gebe keine Lesart, in der die Form in Ordnung
              ist. Wer keinen Schnipsel findet, der knapp danebenliegt, hat sein Muster nicht
              verstanden.
            </span>
          ) : (
            <Notice text={failure} glyph="triangle-alert" color="var(--ac-red)" />
          )}
        </div>
      </Dialog>
    </Modal>
  );
}

/** Die Stelle, aus der die Vorbelegung kommt — Datei, Stand, Zeilen (ADR-0006). */
function siteOf(draft: PatternDraft): string {
  const { path, revisionNumber, startLine, endLine } = draft.finding;
  const lines = startLine === endLine ? `Zeile ${startLine}` : `Zeilen ${startLine}–${endLine}`;

  return `${path} · r${revisionNumber} · ${lines}`;
}

/**
 * Ein mehrzeiliges Feld. Einen mehrzeiligen Baustein gibt es im Design-System
 * nicht, und nachgebaut wird keiner; die Werte für Fläche, Linie und Schrift
 * kommen trotzdem aus den Tokens — so wie bei der Prompt-Vorlage des Agenten.
 */
export function MultilineRow({
  id,
  label,
  value,
  onChange,
  mono = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Code wird in der Schrift für Code gesetzt — Fragment und Beispielpaar. */
  mono?: boolean;
}): ReactElement {
  return (
    <FormRow label={label} htmlFor={id}>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        style={{
          width: "100%",
          minHeight: 76,
          padding: "var(--sp-4) var(--sp-5)",
          background: "var(--surface-field)",
          border: "1px solid var(--border-field)",
          borderRadius: "var(--r-field)",
          color: "var(--fg-1)",
          fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
          fontSize: "var(--fs-12)",
          resize: "vertical",
          outline: "none",
        }}
      />
    </FormRow>
  );
}
